/**
 * Update date: 28-08-2023
 * Popup product detail component
 */
import { DataGridFull } from 'app/components/DataGrid';
import { Button } from 'devextreme-react';
import useFormat from 'hooks/useFormat';
import useFormatTime from 'hooks/useFormatTime';
import { useEffect, useRef, useState } from 'react';
import { useProductDetail } from '..';
import useAxios from 'axios-hooks';
import LoadPanel from 'app/components/LoadPanel';
import moment from 'moment';
import { SPECIAL_DEAL_PRODUCT_TYPES } from 'constant';

function EventHistory() {
  const { t, data, dateFormatStr, productId }: any = useProductDetail();
  const [selected, setSelected] = useState('3days');
  const { DateFormat, DateTimeFormat } = useFormatTime();
  const { AmountFormat, QtyFormat, DoubleFormat } = useFormat();
  const [listData, setListData] = useState([]);
  const dataGridRef: any = useRef(null);
  const onDateSelected = async (option: any) => {
    setSelected(option);
    let startDate = '';
    let endDate = '';
    switch (option) {
      case '3days':
        startDate = dateFormatStr(moment(new Date()).subtract(3, 'days'));
        endDate = dateFormatStr(new Date());
        break;
      case '7days':
        startDate = dateFormatStr(moment(new Date()).subtract(7, 'days'));
        endDate = dateFormatStr(new Date());
        break;
      case '30days':
        startDate = dateFormatStr(moment(new Date()).subtract(30, 'days'));
        endDate = dateFormatStr(new Date());
        break;
      case '90days':
        startDate = dateFormatStr(moment(new Date()).subtract(90, 'days'));
        endDate = dateFormatStr(new Date());
        break;
      case '180days':
        startDate = dateFormatStr(moment(new Date()).subtract(180, 'days'));
        endDate = dateFormatStr(new Date());
        break;
      default:
        break;
    }
    const res = await refetch({
      data: {
        productId: productId,
        startDate,
        endDate,
        type: '3',
      },
    });
    setListData(res?.data?.data?.event);
  };

  const [{ loading }, refetch] = useAxios(
    {
      url: 'product/product/popup-purchase-sale-event',
      method: 'POST',
    },
    {
      manual: true,
      autoCancel: true,
      useCache: false,
    },
  );
  const exportExcel = () => {
    dataGridRef?.current?.instance?.exportToExcel();
  };
  useEffect(() => {
    if (data?.data) {
      setListData(data?.data?.event);
    }
  }, [data?.data]);
  return (
    <>
      <LoadPanel visible={loading} />
      <div className="date-options date-option-premiums">
        <Button
          text={t('Last 3 days')}
          onClick={() => onDateSelected('3days')}
          className={`${
            selected === '3days' && 'popup-product-selected-button'
          }`}
        />
        <Button
          text={t('Last 7 days')}
          onClick={() => onDateSelected('7days')}
          className={`${
            selected === '7days' && 'popup-product-selected-button'
          }`}
        />
        <Button
          text={t('Last 30 days')}
          onClick={() => onDateSelected('30days')}
          className={`${
            selected === '30days' && 'popup-product-selected-button'
          }`}
        />
        <Button
          text={t('Last 90 days')}
          onClick={() => onDateSelected('90days')}
          className={`${
            selected === '90days' && 'popup-product-selected-button'
          }`}
        />
        <Button
          text={t('Last 180 days')}
          onClick={() => onDateSelected('180days')}
          className={`${
            selected === '180days' && 'popup-product-selected-button'
          }`}
        />
      </div>
      <DataGridFull
        storageKeyInquiry="event_history"
        ref={dataGridRef}
        dataSource={listData}
        options={{
          columnAutoWidth: true,
          height: 'calc(100vh - 260px)',
          groupPanel: {
            visible: true,
          },
          export: {
            fileName: 'Event History',
          },
          onToolbarPreparing: (e: any) => {
            e.toolbarOptions.items.unshift(
              {
                location: 'before',
                template: 'totalCount',
              },
              {
                location: 'before',
                widget: 'dxButton',
                options: {
                  icon: 'xlsxfile',
                  text: t('Export to Excel'),
                  onClick: () => exportExcel(),
                },
              },
            );
          },
          summary: {
            totalItems: [],
            groupItems: [
              {
                summaryType: 'count',
                displayFormat: '{0}',
              },
            ],
          },
        }}
        columns={[
          {
            caption: t('Special Deal'),
            alignment: 'center',
            items: [
              {
                dataField: 'product_deal_nm',
                caption: t('Special Deal'),
                showWhenGrouped: true,
              },
              {
                dataField: 'purchase_str_dt',
                caption: t('Purchase Start Date'),
                dataType: 'date',
                format: DateFormat,
                alignment: 'center',
                showWhenGrouped: true,
              },
              {
                dataField: 'purchase_end_dt',
                caption: t('Purchase End Date'),
                dataType: 'date',
                format: DateFormat,
                alignment: 'center',
                showWhenGrouped: true,
              },
              {
                dataField: 'sale_str_dt',
                caption: t('Sale Start Date'),
                dataType: 'date',
                format: DateFormat,
                alignment: 'center',
                showWhenGrouped: true,
              },
              {
                dataField: 'sale_end_dt',
                caption: t('Sale End Date'),
                dataType: 'date',
                format: DateFormat,
                alignment: 'center',
                showWhenGrouped: true,
              },
              {
                dataField: 'use_tf',
                caption: t('Use'),
                alignment: 'center',
                editorType: 'dxSwitch',
              },
            ],
          },
          {
            caption: t('Product'),
            alignment: 'center',
            columns: [
              {
                dataField: 'product_cd',
                caption: t('Product code'),
                showWhenGrouped: true,
                visible: false,
              },
              {
                dataField: 'product_nm',
                caption: t('Product Name'),
                showWhenGrouped: true,
                visible: false,
              },
              {
                dataField: 'marketable_size',
                caption: t('Marketable Size'),
                showWhenGrouped: true,
                visible: false,
              },
              {
                dataField: 'option_nm',
                caption: t('Option'),
                showWhenGrouped: true,
                alignment: 'center',
              },
              {
                dataField: 'quantity_per_pack',
                caption: t('Quantity Per Pack'),
                dataType: 'number',
                format: QtyFormat,
                showWhenGrouped: true,
              },
            ],
          },
          {
            caption: t('Content'),
            alignment: 'center',
            columns: [
              {
                dataField: 'purchase_unit_price',
                caption: t('Purchase Unit Price'),
                dataType: 'number',
                format: AmountFormat,
                showWhenGrouped: true,
              },
              {
                dataField: 'deal_purchase_unit_price',
                caption: t('Deal Purchase Unit Price'),
                dataType: 'number',
                format: AmountFormat,
                showWhenGrouped: true,
              },
              {
                dataField: 'purchase_discount_rate',
                caption: t('Purchase Discount Rate'),
                dataType: 'number',
                format: DoubleFormat,
                showWhenGrouped: true,
              },
              {
                dataField: 'supply_unit_price',
                caption: t('Supply Unit Price'),
                dataType: 'number',
                format: AmountFormat,
                showWhenGrouped: true,
              },
              {
                dataField: 'deal_supply_unit_price',
                caption: t('Deal Supply Unit Price'),
                dataType: 'number',
                format: AmountFormat,
                showWhenGrouped: true,
              },
              {
                dataField: 'supply_discount_rate',
                caption: t('Supply Discount Rate'),
                dataType: 'number',
                format: DoubleFormat,
                showWhenGrouped: true,
              },
              {
                dataField: 'profit_rate',
                caption: t('Profit Rate'),
                dataType: 'number',
                format: DoubleFormat,
                showWhenGrouped: true,
              },
              {
                dataField: 'product_deal_qt',
                caption: t('Qty'),
                dataType: 'number',
                showWhenGrouped: true,
                format: QtyFormat,
              },
              {
                dataField: 'product_deal_tp',
                caption: t('Apply Type'),
                showWhenGrouped: true,
                customizeText: e =>
                  t(
                    `${
                      SPECIAL_DEAL_PRODUCT_TYPES.find(
                        item => e.value == item.value,
                      )?.label || ''
                    }`,
                  ),
              },
            ],
          },
          {
            dataField: 'product_deal_nt',
            caption: t('Deal Note'),
            showWhenGrouped: true,
            visible: false,
          },
          {
            dataField: 'maker',
            caption: t('Maker'),
            showWhenGrouped: true,
            visible: false,
          },
          {
            dataField: 'origin',
            caption: t('Origin'),
            showWhenGrouped: true,
            visible: false,
          },
          {
            dataField: 'vendor_nm_default',
            caption: t('Default Vendor'),
            showWhenGrouped: true,
            visible: false,
          },
          {
            dataField: 'large_category',
            caption: t('Large category'),
            showWhenGrouped: true,
            visible: false,
          },
          {
            dataField: 'medium_category',
            caption: t('Medium category'),
            showWhenGrouped: true,
            visible: false,
          },
          {
            dataField: 'small_category',
            caption: t('Small category'),
            showWhenGrouped: true,
            visible: false,
          },
          {
            dataField: 'sub_category',
            caption: t('Sub category'),
            showWhenGrouped: true,
            visible: false,
          },
          {
            dataField: 'regist_dm',
            caption: t('Register Date&Time'),
            dataType: 'date',
            format: DateTimeFormat,
            showWhenGrouped: true,
            visible: false,
          },
          {
            dataField: 'register_user_nm',
            caption: t('Register User'),
            showWhenGrouped: true,
            visible: false,
          },
          {
            dataField: 'register_program_id',
            caption: t('Register Program ID'),
            alignment: 'left',
            showWhenGrouped: true,
            visible: false,
          },
          {
            dataField: 'modify_dm',
            caption: t('Modify Date&Time'),
            dataType: 'date',
            format: DateTimeFormat,
            showWhenGrouped: true,
            visible: false,
          },
          {
            dataField: 'modify_user_nm',
            caption: t('Modify User'),
            showWhenGrouped: true,
            visible: false,
          },
          {
            dataField: 'modify_program_id',
            caption: t('Modify Program ID'),
            showWhenGrouped: true,
            visible: false,
          },
        ]}
      />
    </>
  );
}

export default EventHistory;
